<script setup lang="ts">
/** The inbox in the top bar: unread count, the latest notifications, open one (marks it read). */
import { Bell, Bot, CheckCheck, ListChecks, Sparkles } from "@lucide/vue";
import { useQueryClient } from "@tanstack/vue-query";
import { computed, ref } from "vue";
import { useRouter } from "vue-router";
import { markAllNotificationsRead, markNotificationRead, notificationKeys, useNotifications, type Notification } from "@/api/notifications";
import EmptyState from "@/components/common/EmptyState.vue";
import RelativeTime from "@/components/common/RelativeTime.vue";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

const router = useRouter();
const queryClient = useQueryClient();
const inbox = useNotifications();
const open = ref(false);

const unread = computed(() => inbox.data.value?.unread ?? 0);
const items = computed(() => inbox.data.value?.items ?? []);
const icon = (n: Notification) => (n.source === "agent" ? Bot : n.source === "todos" ? ListChecks : Sparkles);
const refresh = () => queryClient.invalidateQueries({ queryKey: notificationKeys.all });

async function openOne(n: Notification) {
  if (!n.readAt) await markNotificationRead(n.id).catch(() => undefined);
  void refresh();
  if (n.link) {
    open.value = false;
    if (n.link.startsWith("/")) await router.push(n.link);
    else window.open(n.link, "_blank", "noopener");
  }
}

async function readAll() {
  await markAllNotificationsRead();
  await refresh();
}
</script>

<template>
  <Popover v-model:open="open">
    <PopoverTrigger as-child>
      <Button variant="ghost" size="icon" class="relative" :aria-label="unread ? `Inbox, ${unread} unread` : 'Inbox'">
        <Bell />
        <span v-if="unread" class="absolute top-1 right-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-semibold text-primary-foreground tabular-nums">
          {{ unread > 99 ? "99+" : unread }}
        </span>
      </Button>
    </PopoverTrigger>
    <PopoverContent align="end" class="w-[min(24rem,calc(100vw-2rem))] p-0">
      <div class="flex items-center gap-2 border-b px-3 py-2">
        <span class="text-sm font-medium">Inbox</span>
        <span class="text-xs text-muted-foreground">Schorschi, agents, todos</span>
        <Button v-if="unread" variant="ghost" size="sm" class="ml-auto h-7" @click="readAll"><CheckCheck /> All read</Button>
      </div>
      <EmptyState v-if="!items.length" compact :icon="Bell" title="Nothing yet" description="Schorschi, your agents and todo assignments write here." class="m-3" />
      <ul v-else class="max-h-[60vh] divide-y overflow-y-auto">
        <li v-for="n in items" :key="n.id">
          <button type="button" class="flex w-full gap-2.5 px-3 py-2.5 text-left transition-colors hover:bg-state-hover focus-visible:outline-offset-[-2px] active:bg-state-press" :class="n.readAt ? 'opacity-70' : ''" @click="openOne(n)">
            <component :is="icon(n)" class="mt-0.5 size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
            <span class="flex min-w-0 flex-1 flex-col gap-0.5">
              <span class="flex items-center gap-1.5">
                <span v-if="!n.readAt" class="size-1.5 shrink-0 rounded-full bg-primary" aria-label="unread" />
                <span class="truncate text-sm font-medium">{{ n.title }}</span>
              </span>
              <span v-if="n.bodyMd" class="line-clamp-2 text-xs text-muted-foreground">{{ n.bodyMd.replace(/[#*_`>]/g, "") }}</span>
              <RelativeTime :value="n.createdAt" class="text-[11px] text-muted-foreground" />
            </span>
          </button>
        </li>
      </ul>
    </PopoverContent>
  </Popover>
</template>
